// client/src/services/authService.js - Vollständig auf JWT umgestellt
import api from './api';
import { jwtDecode } from 'jwt-decode';

// Token-Hilfsfunktionen
const getToken = () => localStorage.getItem('token');
const setToken = token => localStorage.setItem('token', token);
const removeToken = () => localStorage.removeItem('token');

// Benutzerdaten aus dem localStorage abrufen/speichern
const getStoredUser = () => {
    const userString = localStorage.getItem('user');
    if (userString) {
        try {
            return JSON.parse(userString);
        } catch (e) {
            console.error('Fehler beim Parsen der Benutzerdaten:', e);
            return null;
        }
    }
    return null;
};

const setStoredUser = user => {
    localStorage.setItem('user', JSON.stringify(user));
};

const removeStoredUser = () => {
    localStorage.removeItem('user');
};

/**
 * Prüft, ob der aktuelle Token gültig ist
 * @returns {boolean} - True, wenn der Token gültig ist
 */
/**
 * Prüft, ob der aktuelle Token gültig ist und stellt sicher, dass er korrekt gesetzt ist
 * @returns {boolean} - True, wenn der Token gültig ist
 */
const isTokenValid = () => {
    const token = getToken();

    if (!token) {
        console.log('Kein Token gefunden');
        return false;
    }

    try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;

        // Debug-Ausgabe für Token-Informationen
        console.log('Token-Info:', {
            exp: decoded.exp,
            currentTime,
            isValid: decoded.exp > currentTime,
            timeLeft: decoded.exp - currentTime
        });

        // Prüfen, ob Token abgelaufen ist
        if (decoded.exp <= currentTime) {
            console.log('Token ist abgelaufen, entferne ihn');
            removeToken();
            return false;
        }

        return true;
    } catch (error) {
        console.error('Fehler beim Dekodieren des Tokens:', error);
        // Bei Fehler Token entfernen, da er wahrscheinlich ungültig ist
        removeToken();
        return false;
    }
};
/**
 * Registriert einen neuen Benutzer
 * @param {Object} userData - Benutzerdaten für die Registrierung
 * @returns {Promise<Object>} - Ergebnis der Registrierung
 */
export const register = async (userData) => {
    try {
        if (!userData.email || !userData.password) {
            return {
                success: false,
                message: 'E-Mail und Passwort sind erforderlich'
            };
        }

        const response = await api.post('/auth/register', userData);

        // Bei erfolgreicher Registrierung Token und Benutzerdaten speichern
        if (response.success && response.token) {
            setToken(response.token);
            setStoredUser(response.user);
        }

        return response;
    } catch (error) {
        console.error('Registrierungsfehler:', error);
        return {
            success: false,
            message: error.message || 'Registrierung fehlgeschlagen'
        };
    }
};

/**
 * Meldet einen Benutzer an
 * @param {Object} credentials - Anmeldedaten (E-Mail und Passwort)
 * @returns {Promise<Object>} - Ergebnis der Anmeldung
 */
export const login = async (credentials) => {
    try {
        if (!credentials.email || !credentials.password) {
            return {
                success: false,
                message: 'E-Mail und Passwort sind erforderlich'
            };
        }

        const response = await api.post('/auth/login', credentials);

        // Bei erfolgreicher Anmeldung Token und Benutzerdaten speichern
        if (response.success && response.token) {
            setToken(response.token);
            setStoredUser(response.user);
        }

        return response;
    } catch (error) {
        console.error('Anmeldefehler:', error);
        return {
            success: false,
            message: error.message || 'Anmeldung fehlgeschlagen'
        };
    }
};

/**
 * Meldet einen Benutzer ab
 * @returns {Promise<Object>} - Ergebnis der Abmeldung
 */
export const logout = async () => {
    try {
        // Token und Benutzerdaten entfernen
        removeToken();
        removeStoredUser();

        return { success: true };
    } catch (error) {
        console.error('Abmeldefehler:', error);
        return {
            success: false,
            message: 'Abmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.'
        };
    }
};

/**
 * Ruft den aktuellen authentifizierten Benutzer ab mit verbesserter Fehlerbehandlung
 * @returns {Promise<Object|null>} - Benutzerdaten oder null, wenn nicht angemeldet
 */
export const getCurrentUser = async () => {
    try {
        // Zuerst prüfen, ob ein Token vorhanden und gültig ist
        if (!isTokenValid()) {
            console.log('Token ist ungültig oder fehlt');
            return null;
        }

        // Gespeicherte Benutzerdaten abrufen
        const storedUser = getStoredUser();

        // Wenn gespeicherte Daten vorhanden sind, diese zurückgeben
        if (storedUser) {
            console.log('Gespeicherte Benutzerdaten gefunden:', storedUser.id || storedUser.uid);
            return storedUser;
        }

        // Andernfalls vom Server abrufen
        console.log('Keine gespeicherten Benutzerdaten, hole vom Server');
        const response = await api.get('/auth/me');

        if (response.success && response.data) {
            console.log('Benutzerdaten vom Server erhalten:', response.data);
            // Benutzerdaten im localStorage speichern
            setStoredUser(response.data);
            return response.data;
        }

        console.log('Keine Benutzerdaten vom Server erhalten');
        return null;
    } catch (error) {
        console.error('Fehler beim Abrufen der Benutzerdaten:', error);
        return null;
    }
};

/**
 * Prüft, ob ein Benutzer authentifiziert ist mit verbesserter Diagnostik
 * @returns {Promise<boolean>} - True, wenn der Benutzer authentifiziert ist
 */
export const isAuthenticated = async () => {
    try {
        console.log('Prüfe Authentifizierungsstatus...');

        if (!isTokenValid()) {
            console.log('Token ungültig, Benutzer ist nicht authentifiziert');
            return false;
        }

        const user = await getCurrentUser();
        const authenticated = !!user;

        console.log('Authentifizierungsstatus:', authenticated);

        // Bei erfolgreicher Authentifizierung: Diagnose-Informationen ausgeben
        if (authenticated) {
            console.log('Authentifizierte Benutzer-ID:', user.id || user.uid);
            console.log('Benutzerrolle:', user.role);
        }

        return authenticated;
    } catch (error) {
        console.error('Fehler bei der Authentifizierungsprüfung:', error);
        return false;
    }
};

/**
 * Ändert das Passwort des aktuellen Benutzers
 * @param {string} currentPassword - Aktuelles Passwort
 * @param {string} newPassword - Neues Passwort
 * @returns {Promise<Object>} - Ergebnis der Passwortänderung
 */
export const changePassword = async (currentPassword, newPassword) => {
    try {
        if (!currentPassword || !newPassword) {
            return {
                success: false,
                message: 'Aktuelles und neues Passwort sind erforderlich'
            };
        }

        const response = await api.put('/auth/change-password', {
            currentPassword,
            newPassword
        });

        return response;
    } catch (error) {
        console.error('Fehler beim Ändern des Passworts:', error);
        return {
            success: false,
            message: error.message || 'Fehler beim Ändern des Passworts'
        };
    }
};

/**
 * Prüft, ob ein Benutzer eine bestimmte Rolle hat
 * @param {string} role - Die zu prüfende Rolle
 * @returns {Promise<boolean>} - True, wenn der Benutzer die Rolle hat
 */
export const hasRole = async (role) => {
    try {
        const user = await getCurrentUser();
        return user && user.role === role;
    } catch (error) {
        console.error('Fehler bei der Rollenprüfung:', error);
        return false;
    }
};

/**
 * Sendet eine E-Mail zur Bestätigung der E-Mail-Adresse
 * @returns {Promise<Object>} - Ergebnis des Sendevorgangs
 */
export const sendVerificationEmail = async () => {
    try {
        const response = await api.post('/auth/send-verification-email');
        return response;
    } catch (error) {
        console.error('Fehler beim Senden der Bestätigungs-E-Mail:', error);
        return {
            success: false,
            message: error.message || 'Fehler beim Senden der Bestätigungs-E-Mail'
        };
    }
};

/**
 * Verifiziert die E-Mail-Adresse mit einem Token
 * @param {string} token - Der Verifizierungstoken aus der E-Mail
 * @returns {Promise<Object>} - Ergebnis der Verifizierung
 */
export const verifyEmail = async (token) => {
    try {
        if (!token) {
            return { success: false, message: 'Kein Token bereitgestellt.' };
        }
        // Der API-Aufruf benötigt keinen Request-Body, nur den Token in der URL
        const response = await api.get(`/auth/verify-email/${token}`);
        return response; // Gibt { success: true, message: '...' } oder Fehler vom Backend zurück
    } catch (error) {
        console.error('Fehler bei der E-Mail-Verifizierung via API:', error);
        return {
            success: false,
            message: error.message || 'API-Fehler bei der E-Mail-Verifizierung'
        };
    }
};

/**
 * Aktualisiert das Benutzerprofil
 * @param {Object} userData - Zu aktualisierende Benutzerdaten
 * @returns {Promise<Object>} - Ergebnis der Aktualisierung
 */
export const updateUserProfile = async (userData) => {
    try {
        const response = await api.put('/users/me', userData);

        if (response.success && response.data) {
            // Aktualisierte Benutzerdaten speichern
            const currentUser = getStoredUser();
            const updatedUser = { ...currentUser, ...response.data };
            setStoredUser(updatedUser);
        }

        return response;
    } catch (error) {
        console.error('Fehler beim Aktualisieren des Benutzerprofils:', error);
        return {
            success: false,
            message: error.message || 'Fehler beim Aktualisieren des Benutzerprofils'
        };
    }
};

/**
 * Sendet eine E-Mail zum Zurücksetzen des Passworts
 * @param {string} email - E-Mail-Adresse des Benutzers
 * @returns {Promise<Object>} - Ergebnis des Passwort-Reset-Vorgangs
 */
export const forgotPassword = async (email) => {
    try {
        if (!email || !email.trim()) {
            return {
                success: false,
                message: 'Bitte geben Sie eine E-Mail-Adresse ein'
            };
        }

        const response = await api.post('/auth/reset-password', { email });
        return response;
    } catch (error) {
        console.error('Fehler beim Zurücksetzen des Passworts:', error);
        return {
            success: false,
            message: error.message || 'Fehler beim Senden der Passwort-Zurücksetz-E-Mail'
        };
    }
};

/**
 * Setzt ein Passwort mit einem Token zurück
 * @param {string} resetToken - Token für das Zurücksetzen des Passworts
 * @param {string} newPassword - Neues Passwort
 * @returns {Promise<Object>} - Ergebnis des Passwort-Reset-Vorgangs
 */
export const resetPassword = async (resetToken, newPassword) => {
    try {
        const response = await api.put(`/auth/reset-password/${resetToken}`, {
            password: newPassword
        });

        return response;
    } catch (error) {
        console.error('Fehler beim Zurücksetzen des Passworts:', error);
        return {
            success: false,
            message: error.message || 'Fehler beim Zurücksetzen des Passworts'
        };
    }
};